/* oxlint-disable react/refs -- imperative refs are required for DOM, focus, drag, and media behavior */
import { useCallback, useRef, useState } from 'react'
import { useUser } from 'app/utils'
import { logWarn } from 'lib/logging'

/**
 * The storage key a persisted selection lives under.
 *
 * Scoped to the user because a browser is not a person: two people sharing a
 * front desk machine each get their own filters rather than inheriting whatever
 * the last shift left switched off.
 */
export function persistedStateKey(key: string, userId: string): string {
  return `trk:${userId}:${key}`
}

function readStored<T>(storageKey: string | undefined, isValid: (value: unknown) => value is T): T | undefined {
  if (!storageKey || typeof window === `undefined`) return undefined
  try {
    const raw = window.localStorage.getItem(storageKey)
    if (raw === null) return undefined
    const parsed = JSON.parse(raw)
    // A value we no longer recognize (a chip kind since renamed, a hand-edited
    // blob) has to lose to the default. Restoring it would filter the feed by
    // something that matches nothing, which reads as missing data.
    return isValid(parsed) ? parsed : undefined
  } catch (error) {
    logWarn(`usePersistedState failed to read`, storageKey, error)
    return undefined
  }
}

/**
 * useState whose value outlives the component, so a filter the user set is still
 * set when they come back to the screen.
 *
 * The stored value is read in the initializer rather than an effect: chips that
 * paint at their defaults and correct themselves a frame later look like the app
 * forgetting and then remembering.
 */
export function usePersistedState<T>(
  key: string,
  defaultValue: T,
  isValid: (value: unknown) => value is T,
): [T, (next: T | ((current: T) => T)) => void] {
  const user = useUser()
  // Nothing is persisted until we know who is asking. The hook still behaves
  // like useState in the meantime, so a component that renders ahead of auth
  // works, it just does not remember that render's choices.
  const storageKey = user?.id ? persistedStateKey(key, user.id) : undefined

  const [state, setState] = useState<{ storageKey: string | undefined; value: T }>(() => ({
    storageKey,
    value: readStored(storageKey, isValid) ?? defaultValue,
  }))

  let value = state.value
  if (storageKey !== state.storageKey) {
    value =
      readStored(storageKey, isValid) ??
      // Arriving at a key for the first time (auth resolving under us) must not
      // throw away a choice already made this session. Moving between two real
      // keys is a genuine context switch, so that one starts from the default.
      (state.storageKey === undefined ? state.value : defaultValue)
    setState({ storageKey, value })
  }

  // Held in a ref as well so two updates in one tick see each other, the way
  // useState's own functional updates do.
  const stateRef = useRef({ storageKey, value })
  stateRef.current = { storageKey, value }

  const setValue = useCallback((next: T | ((current: T) => T)) => {
    const current = stateRef.current
    const resolved = typeof next === `function` ? (next as (c: T) => T)(current.value) : next
    stateRef.current = { storageKey: current.storageKey, value: resolved }
    if (current.storageKey) {
      try {
        window.localStorage.setItem(current.storageKey, JSON.stringify(resolved))
      } catch (error) {
        // A full or blocked store costs the user their memory, not their filter.
        logWarn(`usePersistedState failed to write`, current.storageKey, error)
      }
    }
    setState(stateRef.current)
  }, [])

  return [value, setValue]
}
